import { HeadContent, Outlet, Scripts, createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";

// Side-effect import (not `?url` + manual <link>): TanStack Start runs two
// Rollup passes (client + server), and a `?url` import is hashed independently
// in each. Because index.css embeds hashed @fontsource font URLs, the two
// passes can produce different content-hashes for index.css — the SSR <head>
// then links a hash the client build never emits, which 404s and causes a
// flash of unstyled content. A side-effect import lets Start inject the
// stylesheet <link> from the shared build manifest, so the two passes agree.
import "../index.css";
import { AppCrashBoundary } from "@/components/error-surfaces/app-crash-boundary";
import { DefaultErrorComponent } from "@/components/router-fallbacks";
import { GlobalStateOverlays } from "@/components/global-state-overlays";
import { MaintenanceTakeover } from "@/components/maintenance-takeover";
import type { GlobalStateStore } from "@/lib/global-state";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";

// Auth gating lives on the pathless _authed layout route (routes/_authed.tsx),
// not here: protection is decided by route-tree membership, so the root route
// carries no path allowlist. The QueryClientProvider is supplied by
// setupRouterSsrQueryIntegration (router.tsx).
//
// The global-state store rides the router context beside the QueryClient
// because the two halves of the seam sit on opposite sides of React: the Query
// cache handlers that flip it run outside the tree entirely (router.tsx), and
// this component reads it. Context is the one place both already reach.
export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  globalState: GlobalStateStore;
}>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Lennar Platform" },
    ],
    links: [{ rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" }],
  }),
  shellComponent: RootDocument,
  component: RootLayout,
  errorComponent: DefaultErrorComponent,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Mounted in the shell rather than in RootLayout so it is outside
            everything the router can throw from — RootLayout and its providers,
            every route component, and the errorComponent below, which the root
            match renders *instead of* RootLayout. Without it, a throw in any of
            those unmounts the tree and leaves a blank page.

            This is as wide as a boundary can usefully go, not as wide as the
            document: `<head>`/HeadContent cannot host a visible fallback, and
            wrapping `Scripts` would let a render crash suppress hydration
            instead of recovering from it. The QueryClientProvider that
            setupRouterSsrQueryIntegration adds sits further out still, and the
            only hooks outside it — createRouter's Wrap and InnerWrap — are
            documented as provider-only (a fallback there would replace the
            `<html>` this shell renders). */}
        <AppCrashBoundary>{children}</AppCrashBoundary>
        {import.meta.env.DEV && (
          <TanStackDevtools
            config={{ position: "bottom-right" }}
            plugins={[
              {
                name: "TanStack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
              {
                name: "TanStack Query",
                render: <ReactQueryDevtoolsPanel />,
              },
            ]}
          />
        )}
        <Scripts />
      </body>
    </html>
  );
}

function RootLayout() {
  const { globalState } = Route.useRouteContext();

  return (
    // `skipDelayDuration` is MUI's hysteresis window: every open sets a
    // module-global `hystersisOpen` shared by every `Tooltip` on the page, and
    // a close clears it `800 + leaveDelay` ms later
    // (`Tooltip.js:210-211,305-306,321-322`). While it is
    // set, `enterDelay` is replaced by `enterNextDelay`, which is 0 (`:348-349`):
    // the second tooltip a reader visits opens on contact. Radix has the same
    // clock and defaults it to 300ms (`react-tooltip/dist/index.mjs:31`).
    //
    // After a leave, the window starts at the close itself, as MUI's does,
    // because `MuiTooltip`'s leave timer closes through the primitive (#506).
    // Not after a press: Radix tells the provider a box closed before it asks
    // the tooltip (`index.mjs:90-99`), so a press close that `MuiTooltip`
    // refuses still starts the window at the press, while the box stays up.
    // A reader who then leaves and reaches the next tooltip more than 800ms
    // after the press waits out its open delay, where the donor opens on
    // contact. Pre-dates #506: #480's D5, accepted in its decision round.
    //
    // 800 and not 1000: one donor caller *does* pass a leave delay — the Not
    // Recommended label's `closeDelay={200}`, forwarded as `leaveDelay`
    // (`item-not-recommended.tsx:37`, `react-ui/src/tooltip/tooltip.tsx:80`) —
    // so the donor's window after *that* box closes is 1000ms. Radix has no
    // per-tooltip skip window to say so with, so the provider carries the
    // number every other caller wants, and that one box's window stays 200ms
    // short: a named residue in the parity ledger. Its 200ms close delay is
    // itself carried now (`leaveDelayMs`), where it used to be #254's break.
    <TooltipProvider delayDuration={300} skipDelayDuration={800}>
      {/* The one takeover, around the Outlet itself: with enable_maintenance
          on it replaces the app's rendered content on every path, signed in or
          not — and the facade read is synchronous, so the SSR pass paints the
          maintenance screen first, before anything of the app loads. */}
      <MaintenanceTakeover store={globalState}>
        <Outlet />
        {/* Single app-wide toast outlet. Mounted here so toasts fire from
            every route — and *inside* the takeover, because toasts render in
            a top-layer portal: an active toast fired before a maintenance
            flip would otherwise float over the maintenance screen. Sonner
            renders into a portal, so placement is positional only. */}
        <Toaster />
      </MaintenanceTakeover>
      {/* Above the Outlet, never around it: the global-state overlays render
          on top of whatever route is mounted, so a session that expires
          mid-edit costs the visitor a login and not their work. */}
      <GlobalStateOverlays store={globalState} />
    </TooltipProvider>
  );
}
